<template>
    <v-bottom-sheet v-model="filterOpened" >
        <v-sheet>
            <v-toolbar
                color="var(--main-header)"
                class="flex-grow-0"
                dark
                elevation="2"
                style="z-index: 2"
                tile
                dense
            >
                <v-toolbar-title><div class="filter-image">{{ $t('Select Filter') }}</div></v-toolbar-title>

                <v-spacer/>

                <v-toolbar-items>
                    <v-btn
                        v-show="filterSupportsSelection"
                        small
                        text
                        @click="selectAllFiltered()"
                    >
                        <img src="../../../assets/icons/filter/select_all.svg" :alt="$t('Select All Filtered')" :title="$t('Select All Filtered')" />
                    </v-btn>

                    <v-btn
                        v-show="filterSupportsSelection"
                        small
                        text
                        @click="toggleSelect()"
                    >
                        <img src="../../../assets/icons/filter/select_toggle.svg" :alt="$t('Invert All')" :title="$t('Invert All')" />
                    </v-btn>

                    <v-btn
                        v-show="filterSupportsSelection"
                        small
                        text
                        @click="deselectAll()"
                    >
                        <img src="../../../assets/icons/filter/select_none.svg" :alt="$t('Deselect All')" :title="$t('Deselect All')" />
                    </v-btn>

                    <v-btn
                        small
                        text
                        @click="closeFilter"
                    >
                        <img src="../../../assets/icons/filter/arrow_down.svg" :alt="$t('Close Filter')" :title="$t('Close Filter')" />
                    </v-btn>
                </v-toolbar-items>

                <template v-slot:extension>
                    <v-tabs
                        v-model="currentTab"
                        align-with-title
                    >
                        <v-tabs-slider color="white"></v-tabs-slider>

                        <v-tab
                            v-for="(filter, index) in filters"
                            :key="`${filter.name}_${index}_tab_head`"
                        >
                            {{ filter.title }}
                        </v-tab>
                    </v-tabs>
                </template>
            </v-toolbar>

            <v-tabs-items
                ref="tabs"
                v-model="currentTab"
                class="flex-grow-1"
            >
                <template v-for="(filter, index) in filters">
                    <DataFilterItem
                        v-if="typeof filter.type === 'undefined' || filter.type === 'data'"
                        :key="`${filter.name}_${index}_tab_item`"
                        ref="filter"
                        v-model="internalValue[filter.name]"
                        :resources="filter.data"
                        :resource-name="filter.name"
                        @input="handleFilterSelection(filter.name, $event)"
                    />
                    <StringFilterItem
                        v-if="filter.type === 'string'"
                        :key="`${filter.name}_${index}_tab_item`"
                        ref="filter"
                        v-model="internalValue[filter.name]"
                        :resource-name="filter.name"
                        @input="handleFilterSelection(filter.name, $event)"
                    />
                    <BooleanFilterItem
                        v-if="filter.type === 'boolean'"
                        :key="`${filter.name}_${index}_tab_item`"
                        ref="filter"
                        v-model="internalValue[filter.name]"
                        :resource-name="filter.name"
                        @input="handleFilterSelection(filter.name, $event)"
                    />
                </template>
            </v-tabs-items>

            <v-toolbar
                color="var(--pagination-background)"
                class="flex-grow-0 filter_bottom_buttons"
                elevation="2"
                style="z-index: 2"
                tile
                dense
            >
                <v-toolbar-items>

                    <v-btn
                        v-if="tabCount > 1"
                        @click="previousTab()"
                        color="var(--pagination-background)"
                        elevation="0"
                    >
                        <img src="../../../assets/icons/table/arrow_left.svg" :alt="$t('Previous')" :title="$t('Previous')"> {{ $t('Previous') }}
                    </v-btn>
                    <v-btn
                        v-if="tabCount > 1"
                        @click="nextTab()"
                        color="var(--pagination-background)"
                        elevation="0"
                    >
                        <img src="../../../assets/icons/table/arrow_right.svg" :alt="$t('Next')" :title="$t('Next')"> {{ $t('Next') }}
                    </v-btn>
                    <v-spacer/>
                    <v-btn
                        v-if="allowGroupingResults && showCompleteResultGroup"
                        color="var(--pagination-background)"
                        elevation="0"
                        @click="showCompleteResultGroup = !showCompleteResultGroup">
                        <img src="../../../assets/icons/filter/grouped_results.svg" :alt="$t('Show complete group')" :title="$t('Show complete group')"> {{ $t('Show complete group') }}
                    </v-btn>
                    <v-btn
                        v-else-if="allowGroupingResults"
                        color="var(--pagination-background)"
                        elevation="0"
                        @click="showCompleteResultGroup = !showCompleteResultGroup">
                        <img src="../../../assets/icons/filter/ungrouped_results.svg" :alt="$t('Show single results')" :title="$t('Show single results')"> {{ $t('Show single results') }}
                    </v-btn>

                </v-toolbar-items>

                <v-spacer/>

                <v-toolbar-items>

                    <v-btn
                        :disabled="!hasFilters"
                        @click="resetFilters()"
                        color="var(--pagination-background)"
                        elevation="0"
                    >
                        {{ $t('Reset Filter') }}
                    </v-btn>
                </v-toolbar-items>
            </v-toolbar>
        </v-sheet>
    </v-bottom-sheet>
</template>

<script>
import DataFilterItem from "./DataFilterItem.vue";
import Vue from "vue";
import BooleanFilterItem from "./BooleanFilterItem.vue";
import StringFilterItem from "./StringFilterItem.vue";

export default {
    name: "TableFilters",
    components: {BooleanFilterItem, DataFilterItem, StringFilterItem},
    props: {
        filters: {
            type: Array
        },
        allowGroupingResults: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            currentTab: 0,
            internalValue: {},
            ignoreDependencies: false,
            activeFilter: false,
            showCompleteResultGroup: false,
        };
    },
    created() {
        for (let index in this.filters) {
            let filter = this.filters[index]
            let defaultValue = []; // type: data
            if (filter.type === 'string') {
                defaultValue = '';
            } else if (filter.type === 'boolean') {
                defaultValue = null;
            }
            Vue.set(this.internalValue, filter.name, defaultValue)
        }
    },
    computed: {
        filterOpened: {
            get() {
                return this.activeFilter !== false
            },
            set() {
                this.activeFilter = false;
            }
        },

        hasFilters() {
            let filterTypes = this.getFilterTypes();
            for (let filter in this.internalValue) {
                if (
                    (
                        (
                            typeof filterTypes[filter] === 'undefined'
                            || filterTypes[filter] === 'data'
                            || filterTypes[filter] === 'string'
                        )
                        && this.internalValue[filter].length
                    ) || (
                        filterTypes[filter] === 'boolean'
                        && this.internalValue[filter] !== null
                    )
                ) {
                    return true;
                }
            }
            return false;
        },

        tabCount() {
            return Object.keys(this.filters).length;
        },

        filterSupportsSelection()
        {
            let filter = this.filters[this.currentTab];
            return typeof filter === 'object'
                && typeof filter.data === 'object'
                && Array.isArray(filter.data)
                && filter.data.length > 0;
        }
    },
    watch: {
        internalValue: {
            handler(newValue) {
                this.$emit('change', newValue, this.showCompleteResultGroup);
            },
            deep: true,
        },

        showCompleteResultGroup: function(value) {
            this.$emit('change', this.internalValue, value);
        },

        activeFilter: function (value) {
            let found = false;
            this.filters.forEach((filter, index) => {
                if (filter.name === value) {
                    found = true;
                    this.currentTab = index;
                }
            });
            if (!found) {
                this.currentTab = 0;
                this.activeFilter = false;
            }
        }

    },
    mounted() {
        this.$emit('change', this.internalValue, this.showCompleteResultGroup);
    },
    methods: {
        nextTab() {
            this.currentTab++;
            if (this.currentTab >= this.tabCount) {
                this.currentTab = 0;
            }
        },

        previousTab() {
            this.currentTab--;
            if (this.currentTab < 0) {
                this.currentTab = this.tabCount - 1;
            }
        },

        deselectAll() {
            this.$refs.filter[this.currentTab].deselectAll();
        },

        selectAllFiltered() {
            this.$refs.filter[this.currentTab].selectAllFiltered();
        },

        toggleSelect() {
            this.$refs.filter[this.currentTab].toggleSelect();
        },

        resetFilters() {
            let filters = Object.keys(this.internalValue);
            filters.forEach(filter => {
                Vue.set(this.internalValue, filter, []);
            });

            this.$nextTick(() => {
                this.currentTab = 0;
            })
        },

        handleFilterSelection(filterName, filterValue) {
            Vue.set(this.internalValue, filterName, filterValue);
        },

        closeFilter() {
            this.filterOpened = false;
        },

        getFilterTypes()
        {
            let types = {};
            for (let index in this.filters) {
                let filter = this.filters[index]
                types[filter.name] = filter.type;
            }
            return types;
        }
    }
}
</script>

<style scoped>
    div.filter-image {
        background: url("../../../assets/icons/filter/filter.svg") no-repeat;
        background-size: 32px 32px;
        height: 32px;
        padding-left: 60px;
    }
</style>
<style>
    header.filter_bottom_buttons button.v-btn {
        color: var(--toolbar-label);
    }
    header.filter_bottom_buttons button.v-btn:disabled {
        color: var(--toolbar-label) !important;
        opacity: 0.5;
    }
    .theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
        background-color: transparent !important;
    }
</style>